/**
 * Maestro Radio — Frontend Service Layer
 *
 * All API calls for the intelligent radio session system.
 * // iOS PARITY: Every endpoint here needs an iOS equivalent.
 */

import api from './api';

export interface TasteProfile {
  favoriteArtists: string[];
  favoriteGenres: string[];
  listenDepth: 'familiar' | 'exploring';
  timeOfDay: 'morning' | 'afternoon' | 'evening' | 'late_night';
  recentlyPlayed: string[];
  energyPreference: number;
}

export interface ScoredTrack {
  id: string;
  title: string;
  artist: string;
  artistId?: string;
  albumTitle?: string;
  albumId?: string;
  coverUrl?: string;
  duration?: number;
  bitDepth?: number;
  sampleRate?: number;
  score: number;
  reason: string;
}

export interface RadioSession {
  id: string;
  userId: string;
  seedType: string;
  seedValue: string;
  tracks: ScoredTrack[];
  createdAt: string;
}

export type FeedbackType = 'skip' | 'full_listen' | 'heart' | 'add_to_library';

export interface TuningParams {
  vocalWeight: number;
  energyLevel: number;
  tempoFeel: number;
  variety: number;
  eraFlexibility: number;
}

export interface SavedStation {
  id: string;
  name: string;
  seedType: string;
  seedValue: string;
  description: string;
  imageUrl: string;
  createdAt: string;
  lastPlayedAt: string | null;
  playCount: number;
  tuningParams?: TuningParams;
  tweaks?: string[];
}

export const maestroRadioService = {
  createSession: (seedType: string, seedValue: string, tuningParams?: TuningParams, extra?: { discoveryMode?: string; personalMode?: string }) =>
    api.post('/api/maestro-radio/session', { seedType, seedValue, tuningParams, ...extra }) as Promise<{
      session: RadioSession;
      profile: TasteProfile;
      stationId: string;
      station: SavedStation;
    }>,

  getNextTracks: (seedType: string, seedValue: string, excludeTrackIds: string[], tuningParams?: TuningParams, extra?: Record<string, any>) =>
    api.post('/api/maestro-radio/next', { seedType, seedValue, excludeTrackIds, tuningParams, ...extra }) as Promise<{
      tracks: ScoredTrack[];
    }>,

  getTasteProfile: () =>
    api.get('/api/maestro-radio/taste-profile') as Promise<{ profile: TasteProfile }>,

  sendFeedback: (params: {
    sessionId?: string;
    trackId: string;
    feedbackType: FeedbackType;
    playDurationSeconds?: number;
    trackTitle?: string;
    trackArtist?: string;
    tuningParams?: TuningParams;
  }) => api.post('/api/maestro-radio/feedback', params) as Promise<{ ok: boolean }>,

  getStations: () =>
    api.get('/api/maestro-radio/stations') as Promise<{ stations: SavedStation[] }>,

  saveStation: (params: {
    seedType: string;
    seedValue: string;
    name?: string;
    imageUrl?: string;
  }) => api.post('/api/maestro-radio/stations', params) as Promise<{ station: SavedStation }>,

  updateStation: (stationId: string, params: Partial<Pick<SavedStation, 'name' | 'imageUrl' | 'seedType' | 'seedValue'>> & { tuningParams?: TuningParams }) =>
    api.put(`/api/maestro-radio/stations/${stationId}`, params) as Promise<{ station: SavedStation }>,

  deleteStation: (stationId: string) =>
    api.delete(`/api/maestro-radio/stations/${stationId}`) as Promise<{ ok: boolean }>,

  playStation: (stationId: string) =>
    api.post(`/api/maestro-radio/stations/${stationId}/play`, {}) as Promise<{
      session: RadioSession;
      profile: TasteProfile;
      station: SavedStation;
      tuningParams?: TuningParams;
    }>,

  updateStationTuning: (stationId: string, tuningParams: TuningParams, sessionId?: string) =>
    api.patch(`/api/maestro-radio/stations/${stationId}/tuning`, { tuningParams, sessionId }) as Promise<{
      station: SavedStation;
      tuningParams: TuningParams;
    }>,

  tweakStation: (stationId: string, tweak: string) =>
    api.post(`/api/maestro-radio/stations/${stationId}/tweak`, { tweak }) as Promise<{
      station: SavedStation;
      ellaResponse: string;
    }>,

  removeTweak: (stationId: string, removeTweakIndex: number) =>
    api.post(`/api/maestro-radio/stations/${stationId}/tweak`, { removeTweakIndex }) as Promise<{
      station: SavedStation;
      ellaResponse: null;
    }>,

  reorderStations: (stationIds: string[]) =>
    api.put('/api/maestro-radio/stations/reorder', { stationIds }) as Promise<{ ok: boolean }>,

  getAmbientContext: (lat?: number | null, lon?: number | null, tzOffset?: number, bedtime?: number | null, activeSession?: boolean) =>
    api.get(`/api/maestro-radio/ambient-context?${[
      lat != null ? `lat=${lat}` : '',
      lon != null ? `lon=${lon}` : '',
      `tzOffset=${tzOffset || 0}`,
      bedtime != null ? `bedtime=${bedtime}` : '',
      activeSession ? 'activeSession=1' : '',
    ].filter(Boolean).join('&')}`) as Promise<any>,

  detectLocation: () =>
    api.get('/api/maestro-radio/detect-location') as Promise<{
      city: string | null;
      region: string | null;
      country: string | null;
      lat: number | null;
      lon: number | null;
      source: string;
      accuracy: string;
    }>,
};

export default maestroRadioService;
